import React, { useEffect, useMemo, useState } from "react";
import MainLayout from "../../Layout/mainLayout";
import { Link, useNavigate, useParams } from "react-router-dom";
import "./addCityHead.css";
import {
    addCityHead,
    getFranchiseCityList,
    getFranchiseLocalityList,
    getFranchiseProjectListScoped,
} from "../../Redux/Actions/franchiseManagerAction";
import { useDispatch, useSelector } from "react-redux";
import toast from "react-hot-toast";
import SearchableMultiSelect from "../../Global Functions/SearchableMultiSelect";

const AddCityHead = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { franchise_id } = useParams();

    const {
        cityList: rawCityList,
        localityList: rawLocalityList,
        projectList: rawProjectList,
        cityHeadAdding = false,
    } = useSelector((state) => state.franchiseManager || {});

    const cities = Array.isArray(rawCityList) ? rawCityList : [];
    const localities = Array.isArray(rawLocalityList) ? rawLocalityList : [];
    const projects = Array.isArray(rawProjectList) ? rawProjectList : [];

    const [errors, setErrors] = useState({});
    const [cityHeadData, setCityHeadData] = useState({
        name: "",
        email: "",
        country_code: "+91",
        contact_number: "",
        city: [],
        locality: [],
        project: [],
    });

    useEffect(() => {
        if (franchise_id) {
            dispatch(getFranchiseCityList({ franchise_id }, false));
            dispatch(getFranchiseLocalityList({ franchise_id }, false));
            dispatch(getFranchiseProjectListScoped({ franchise_id }, false));
        }
    }, [franchise_id, dispatch]);

    const cityOptions = useMemo(
        () => cities.map((c) => ({ value: c._id, label: c.city_name })),
        [cities]
    );

    // localities from franchiseLocalityList aren't grouped — city is a raw id, filter client-side
    const localityOptions = useMemo(() => {
        return localities
            .filter((loc) => cityHeadData.city.includes(loc.city))
            .map((loc) => ({
                value: loc._id,
                label: loc.name,
                hint: loc.available === false ? "Taken" : undefined,
                disabled: loc.available === false,
            }));
    }, [localities, cityHeadData.city]);

    // Only projects belonging to a currently-selected locality
    const projectOptions = useMemo(() => {
        return projects
            .filter((p) => {
                const projLocalityIds = (p.localities || []).map((l) => l._id || l);
                return projLocalityIds.some((lid) => cityHeadData.locality.includes(lid));
            })
            .map((p) => ({
                value: p._id,
                label: p.project_name,
                group: p.city?.city_name,
                hint: p.available === false ? "Taken" : undefined,
                disabled: p.available === false,
            }));
    }, [projects, cityHeadData.locality]);

    const clearError = (field) => {
        setErrors((prev) => {
            const next = { ...prev };
            delete next[field];
            return next;
        });
    };

    const handleChange = (event) => {
        const { name, value } = event.target;
        setCityHeadData((prev) => ({ ...prev, [name]: value }));
        clearError(name);
    };

    const handleCityChange = (values) => {
        setCityHeadData((prev) => ({
            ...prev,
            city: values,
            // drop localities that no longer belong to a selected city
            locality: prev.locality.filter((locId) => {
                const loc = localities.find((l) => l._id === locId);
                return loc && values.includes(loc.city);
            }),
            project: [],
        }));
        clearError("city");
    };

    const handleLocalityChange = (values) => {
        setCityHeadData((prev) => {
            // Auto-select every available project belonging to a newly selected locality
            const autoSelected = projects
                .filter((p) => {
                    if (p.available === false) return false;
                    const projLocalityIds = (p.localities || []).map((l) => l._id || l);
                    return projLocalityIds.some((lid) => values.includes(lid));
                })
                .map((p) => p._id);

            // Keep existing picks that still belong to a selected locality
            const kept = prev.project.filter((projId) => {
                const proj = projects.find((p) => p._id === projId);
                if (!proj) return false;
                const projLocalityIds = (proj.localities || []).map((l) => l._id || l);
                return projLocalityIds.some((lid) => values.includes(lid));
            });

            return {
                ...prev,
                locality: values,
                project: values.length === 0 ? [] : Array.from(new Set([...kept, ...autoSelected])),
            };
        });
        clearError("locality");
    };

    const handleProjectChange = (values) => {
        setCityHeadData((prev) => ({ ...prev, project: values }));
        clearError("project");
    };

    const validate = () => {
        const newErrors = {};
        if (!cityHeadData.name.trim()) newErrors.name = "Name is required";
        if (!cityHeadData.email.trim()) newErrors.email = "Email is required";
        if (!cityHeadData.contact_number.trim()) newErrors.contact_number = "Contact number is required";
        if (!cityHeadData.city.length) newErrors.city = "At least one city is required";
        if (!cityHeadData.locality.length) newErrors.locality = "At least one locality is required";
        if (!cityHeadData.project.length) newErrors.project = "At least one project is required";
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleReset = () => {
        setCityHeadData({
            name: "",
            email: "",
            country_code: "+91",
            contact_number: "",
            city: [],
            locality: [],
            project: [],
        });
        setErrors({});
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!validate()) return;

        try {
            const payload = {
                franchise_id,
                name: cityHeadData.name,
                email: cityHeadData.email,
                country_code: cityHeadData.country_code,
                contact_number: cityHeadData.contact_number,
                city: cityHeadData.city,
                locality: cityHeadData.locality,
                project: cityHeadData.project,
            };

            const response = await dispatch(addCityHead(payload));

            if (response?.statusCode === 200) {
                toast.success("City Head added successfully!");
                handleReset();
                navigate(`/franchise/${franchise_id}/city-heads`);
            }
        } catch (error) {
            const errorMessage =
                error?.response?.data?.message || error?.message || "Failed to add city head";

            if (errorMessage.toLowerCase().includes("duplicate") || errorMessage.toLowerCase().includes("already")) {
                setErrors((prev) => ({ ...prev, email: "Email already exists" }));
                return toast.error("Email already exists");
            }

            toast.error(errorMessage);
        }
    };

    return (
        <MainLayout>
            <div className="ach-wrapper">
                <div className="ach-header">
                    <div className="ach-header-left">
                        <div className="ach-header-icon">
                            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                            </svg>
                        </div>
                        <div>
                            <h1 className="ach-title">Add City Head</h1>
                            <p className="ach-subtitle">Assign a city head under this franchise manager</p>
                        </div>
                    </div>

                    <Link to={`/franchise/${franchise_id}/city-heads`} className="ach-view-btn">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                            <path d="M3 21h18" />
                            <path d="M5 21V7l7-4 7 4v14" />
                        </svg>
                        Back to Franchise
                    </Link>
                </div>

                <form className="ach-form" onSubmit={handleSubmit}>
                    <div className="ach-section">
                        <div className="ach-section-header">
                            <span className="ach-section-badge">01</span>
                            <h2 className="ach-section-title">City Head Details</h2>
                        </div>

                        <div className="ach-grid ach-grid-2">
                            <div className="ach-field">
                                <label className="ach-label">
                                    Name <span className="ach-req">*</span>
                                </label>
                                <input
                                    className={`ach-input ${errors.name ? "ach-input--error" : ""}`}
                                    type="text"
                                    name="name"
                                    value={cityHeadData.name}
                                    onChange={handleChange}
                                    placeholder="e.g. Rahul Sharma"
                                    disabled={cityHeadAdding}
                                />
                                {errors.name && <span className="ach-error-msg">{errors.name}</span>}
                            </div>

                            <div className="ach-field">
                                <label className="ach-label">
                                    Email <span className="ach-req">*</span>
                                </label>
                                <input
                                    className={`ach-input ${errors.email ? "ach-input--error" : ""}`}
                                    type="email"
                                    name="email"
                                    value={cityHeadData.email}
                                    onChange={handleChange}
                                    placeholder="e.g. rahul@example.com"
                                    disabled={cityHeadAdding}
                                />
                                {errors.email && <span className="ach-error-msg">{errors.email}</span>}
                            </div>

                            <div className="ach-field">
                                <label className="ach-label">
                                    Country Code <span className="ach-req">*</span>
                                </label>
                                <select
                                    className="ach-select"
                                    name="country_code"
                                    value={cityHeadData.country_code}
                                    onChange={handleChange}
                                    disabled={cityHeadAdding}
                                >
                                    <option value="+91">India (+91)</option>
                                    <option value="+1">USA (+1)</option>
                                    <option value="+971">UAE (+971)</option>
                                </select>
                            </div>

                            <div className="ach-field">
                                <label className="ach-label">
                                    Contact Number <span className="ach-req">*</span>
                                </label>
                                <input
                                    className={`ach-input ${errors.contact_number ? "ach-input--error" : ""}`}
                                    type="text"
                                    name="contact_number"
                                    value={cityHeadData.contact_number}
                                    onChange={handleChange}
                                    placeholder="e.g. 9876543211"
                                    disabled={cityHeadAdding}
                                />
                                {errors.contact_number && <span className="ach-error-msg">{errors.contact_number}</span>}
                            </div>

                            <SearchableMultiSelect
                                label="City"
                                required
                                placeholder="Search cities..."
                                disabled={cityHeadAdding || cityOptions.length === 0}
                                options={cityOptions}
                                selected={cityHeadData.city}
                                onChange={handleCityChange}
                                error={errors.city}
                                emptyMessage="No cities assigned to this franchise manager"
                            />

                            <div className="ach-field-with-action">
                                <button
                                    type="button"
                                    className="ach-select-all-btn"
                                    onClick={() => {
                                        const availableValues = localityOptions.filter((l) => !l.disabled).map((l) => l.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => cityHeadData.locality.includes(v));
                                        handleLocalityChange(allSelected ? [] : availableValues);
                                    }}
                                    disabled={
                                        cityHeadData.city.length === 0 ||
                                        cityHeadAdding ||
                                        localityOptions.filter((l) => !l.disabled).length === 0
                                    }
                                >
                                    {(() => {
                                        const availableValues = localityOptions.filter((l) => !l.disabled).map((l) => l.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => cityHeadData.locality.includes(v));
                                        return allSelected ? "Unselect All" : "Select All";
                                    })()}
                                </button>

                                <SearchableMultiSelect
                                    label="Locality"
                                    required
                                    placeholder={cityHeadData.city.length === 0 ? "Select city first" : "Search localities..."}
                                    disabled={cityHeadData.city.length === 0 || cityHeadAdding}
                                    options={localityOptions}
                                    selected={cityHeadData.locality}
                                    onChange={handleLocalityChange}
                                    error={errors.locality}
                                    emptyMessage="No localities for the selected city"
                                />
                            </div>

                            <div className="ach-field-with-action">
                                <button
                                    type="button"
                                    className="ach-select-all-btn"
                                    onClick={() => {
                                        const availableValues = projectOptions.filter((p) => !p.disabled).map((p) => p.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => cityHeadData.project.includes(v));
                                        handleProjectChange(allSelected ? [] : availableValues);
                                    }}
                                    disabled={
                                        cityHeadData.locality.length === 0 ||
                                        cityHeadAdding ||
                                        projectOptions.filter((p) => !p.disabled).length === 0
                                    }
                                >
                                    {(() => {
                                        const availableValues = projectOptions.filter((p) => !p.disabled).map((p) => p.value);
                                        const allSelected =
                                            availableValues.length > 0 && availableValues.every((v) => cityHeadData.project.includes(v));
                                        return allSelected ? "Unselect All" : "Select All";
                                    })()}
                                </button>

                                <SearchableMultiSelect
                                    label="Project"
                                    required
                                    placeholder={cityHeadData.locality.length === 0 ? "Select locality first" : "Search projects..."}
                                    disabled={cityHeadData.locality.length === 0 || cityHeadAdding}
                                    options={projectOptions}
                                    selected={cityHeadData.project}
                                    onChange={handleProjectChange}
                                    error={errors.project}
                                    emptyMessage="No projects for the selected locality"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="ach-footer">
                        <button type="submit" className="ach-btn ach-btn--submit" disabled={cityHeadAdding}>
                            {cityHeadAdding ? "Saving..." : "Save City Head"}
                        </button>

                        <Link to={`/franchise/${franchise_id}/city-heads`} className="ach-btn ach-btn--cancel" onClick={handleReset}>
                            Cancel
                        </Link>
                    </div>
                </form>
            </div>
        </MainLayout>
    );
};

export default AddCityHead;